import { useState } from 'react'
import { Image } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { OfferModel } from '@/types'

interface Props {
  values: OfferModel.UiContent
  onChange: (ui_content: OfferModel.UiContent) => void
}

export const CampaignUiContentEditor = ({ values, onChange }: Props) => {
  const [mediaOpen, setMediaOpen] = useState(false)
  const update = (patch: Partial<OfferModel.UiContent>) => onChange({ ...values, ...patch })

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-2">
        These settings control the page's SEO metadata, social share preview, and the accent colour
        used across all blocks. The on-page layout is built in Page blocks.
      </p>

      {/* Social / OG image */}
      <div className="space-y-1.5">
        <Label>Social share image</Label>
        <div
          className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-4 transition-colors hover:bg-subdued"
          onClick={() => setMediaOpen(true)}
        >
          {values.hero_image ? (
            <img
              src={values.hero_image}
              alt="Hero"
              className="max-h-32 rounded-md object-cover"
            />
          ) : (
            <>
              <Image className="size-6 text-muted-foreground" />
              <span className="text-sm text-text-2">Click to select from media library</span>
            </>
          )}
        </div>
        {values.hero_image && (
          <Button variant="outline" onClick={() => update({ hero_image: undefined })}>
            Remove image
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>Page title (SEO)</Label>
        <Input
          value={values.page_title ?? ''}
          onChange={(e) => update({ page_title: e.target.value })}
          placeholder="e.g. Summer sale — falls back to the campaign name"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Page description (SEO)</Label>
        <Textarea
          value={values.page_description ?? ''}
          onChange={(e) => update({ page_description: e.target.value })}
          placeholder="Meta description for search engines and social shares"
          rows={3}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Accent colour</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={values.accent_color ?? '#0D9E6E'}
            onChange={(e) => update({ accent_color: e.target.value })}
            className="size-8 shrink-0 cursor-pointer rounded-md border border-input bg-card p-0.5"
          />
          <Input
            value={values.accent_color ?? ''}
            onChange={(e) => update({ accent_color: e.target.value })}
            placeholder="#0D9E6E"
            className="flex-1"
          />
        </div>
      </div>

      <MediaGalleryModal
        open={mediaOpen}
        onOpenChange={setMediaOpen}
        multiple={false}
        onSelect={(files) => {
          if (files[0]?.url) {
            update({ hero_image: files[0].url })
          }
          setMediaOpen(false)
        }}
      />
    </div>
  )
}
